import React, { useId, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useZakeke, type AIPreset } from '@zakeke/zakeke-configurator-react';
import { MOBILE_BREAKPOINT } from '../Store';
import { ReactComponent as GenerateIcon } from '../../assets/svg/arrow-send-right-empty.svg';
import { ReactComponent as ChevronDownIcon } from '../../assets/svg/chevron-down-solid.svg';
import { ReactComponent as CloseIcon } from '../../assets/svg/times-solid.svg';
import { PromptInput } from './PromptInput';
import type { ImageUrlByPresetType, PresetType } from './useAIProductStagingData';
import { useDropdownManager } from '../widgets/dropdownPortal';
import { OptionsPanelDesktop } from './OptionsPanelDesktop';
import { OptionsPanelMobile } from './OptionsPanelMobile';
import { useIsMobile } from './hooks';
import { useMobileFixedDropdopwnOpen } from './FixedDropdown';

interface ControlsProps {
	modalType: 'product-staging' | 'virtual-tryon';
	prompt: string;
	onPromptChange: (value: string) => void;
	activePanel: string | null;
	onPanelToggle: (panelId: string | null) => void;
	onGenerate: () => void;
	isLoadingGenerate?: boolean;
	selectedOptions: {
		background?: AIPreset;
		surface?: AIPreset;
		mood?: AIPreset;
		effect?: AIPreset;
		person?: AIPreset;
		pose?: AIPreset;
	};
	options?: {
		background?: any;
		surface?: any;
		mood?: any;
		effect?: any;
		person?: any;
		pose?: any;
	};
	isLoadingOptions?: boolean;
	selectedValues?: {
		background?: number | null;
		surface?: number | null;
		mood?: number | null;
		effect?: number | null;
		person?: number | null;
		pose?: number | null;
	};
	onSelect?: (type: PresetType, value: number | null) => void;
	imageUrl?: ImageUrlByPresetType;
	setImageUrl?: (value: ImageUrlByPresetType | ((prev: ImageUrlByPresetType) => ImageUrlByPresetType)) => void;
	handleUpload?: (presetType: PresetType) => void;
	hasUserUpload?: (presetType: PresetType) => boolean;
	requiredPanels?: PresetType[];
	allowCustomBackground?: boolean;
	inputImage?: string | null;
	onClearInputImage?: () => void;
	onWebcamOpen?: () => void;
	inputThumbnailUrl?: string | null;
	onExpandInputView?: () => void;
}

interface OptionButtonItemProps {
	label: string;
	shortLabel: string;
	thumbnail?: string | null;
	icon?: React.ReactNode;
	isActive: boolean;
	isUploading?: boolean;
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	menuId: string;
}

const OptionButtonItem: React.FC<OptionButtonItemProps> = ({
	label,
	shortLabel,
	thumbnail,
	icon,
	isActive,
	isUploading = false,
	onClick,
	menuId,
}) => {
	return (
		<OptionButton
			active={isActive}
			onClick={onClick}
			disabled={isUploading}
			aria-haspopup='dialog'
			aria-expanded={isActive}
			aria-label={label}
			aria-controls={menuId}
		>
			{isUploading ? (
				<ThumbnailLoadingSpinner />
			) : thumbnail ? (
				<Thumbnail>
					<img src={thumbnail} alt={label} />
				</Thumbnail>
			) : icon ? (
				<IconThumbnail>{icon}</IconThumbnail>
			) : (
				<EmptyThumbnail>
					<EmptyLine />
				</EmptyThumbnail>
			)}
			<DesktopLabel>{label}</DesktopLabel>
			<MobileLabel>{shortLabel}</MobileLabel>
			<ChevronIcon active={isActive}>
				<ChevronDownIcon />
			</ChevronIcon>
		</OptionButton>
	);
};

export const Controls: React.FC<ControlsProps> = ({
	modalType,
	prompt,
	onPromptChange,
	activePanel,
	onPanelToggle,
	onGenerate,
	isLoadingGenerate = false,
	selectedOptions,
	options,
	isLoadingOptions = false,
	selectedValues,
	onSelect,
	imageUrl,
	setImageUrl,
	handleUpload,
	hasUserUpload,
	allowCustomBackground = true,
	inputImage,
	onClearInputImage,
	onWebcamOpen,
	inputThumbnailUrl,
	onExpandInputView,
}) => {
	const { T_static } = useZakeke();
	const T = useMemo(() => ({ _: T_static }), [T_static]);
	const isMobile = useIsMobile();
	const { showDropdown, closeDropdown, dropdowns } = useDropdownManager();
	const [mobileOptionPanelId, setMobileOptionPanelId] = useState<null | string>(null);
	const dropdownId = 'options-dropdown';
	const innerMenuId = useId();
	const isInsert = useMobileFixedDropdopwnOpen();

	const handleOptionPanelClose = () => (isMobile ? setMobileOptionPanelId(null) : closeDropdown(dropdownId));

	const renderOptionPanel = (panelId: string, element?: HTMLElement) => {
		const option = optionButtons.find((o) => o.panelId === panelId);
		const title = option ? option.label : '';
		const required = !!option?.required;

		const OptionPanelComponent = isMobile ? OptionsPanelMobile : OptionsPanelDesktop;

		return (
			<OptionPanelComponent
				{...((isMobile ? {} : { triggerElement: element }) as any)}
				activePanel={panelId}
				title={title}
				onClose={() => {
					handleOptionPanelClose();
					onPanelToggle(null);
				}}
				options={options || {}}
				isLoading={isLoadingOptions}
				selectedValues={selectedValues || {}}
				setImageUrl={setImageUrl}
				onSelect={(...args) => {
					handleOptionPanelClose();
					onPanelToggle(null);
					onSelect?.(...args);
				}}
				imageUrl={imageUrl}
				handleUpload={(e) => {
					handleOptionPanelClose();
					onPanelToggle(null);
					handleUpload?.(e);
				}}
				supportsUpload={hasUserUpload ? hasUserUpload(panelId as PresetType) : false}
				required={required}
				allowCustomBackground={allowCustomBackground}
				modalType={modalType}
				menuId={innerMenuId}
				onWebcamOpen={() => {
					handleOptionPanelClose();
					onPanelToggle(null);
					onWebcamOpen?.();
				}}
			/>
		);
	};

	const handleOptionClick = (panelId: string, e: React.MouseEvent<HTMLButtonElement>) => {
		if (isMobile) {
			onPanelToggle(panelId);
			setMobileOptionPanelId(panelId);
		} else {
			onPanelToggle(panelId);
			const element = e.currentTarget;
			const optionPanel = renderOptionPanel(panelId, element);
			showDropdown(dropdownId, optionPanel);
		}
	};

	const productStagingButtons = [
		{
			panelId: 'background',
			label: T._('Background'),
			shortLabel: T._('BG'),
			thumbnail: imageUrl?.background?.selectedUrl || selectedOptions.background?.previewUrl,
			isUploading: imageUrl?.background?.isUploading,
			required: true,
		},
		{
			panelId: 'surface',
			label: T._('Surface'),
			shortLabel: T._('Surface'),
			thumbnail: selectedOptions.surface?.previewUrl,
			isUploading: imageUrl?.surface?.isUploading,
		},
		{
			panelId: 'mood',
			label: T._('Mood'),
			shortLabel: T._('Mood'),
			thumbnail: selectedOptions.mood?.previewUrl,
			isUploading: imageUrl?.mood?.isUploading,
		},
	];

	const virtualTryonButtons = [
		{
			panelId: 'person',
			label: T._('Person'),
			shortLabel: T._('Person'),
			thumbnail: imageUrl?.person?.selectedUrl || selectedOptions.person?.previewUrl,
			isUploading: imageUrl?.person?.isUploading,
			required: true,
		},
		{
			panelId: 'pose',
			label: T._('Pose'),
			shortLabel: T._('Pose'),
			thumbnail: selectedOptions.pose?.previewUrl,
			isUploading: imageUrl?.pose?.isUploading,
		},
		{
			panelId: 'background',
			label: T._('Background'),
			shortLabel: T._('BG'),
			thumbnail: imageUrl?.background?.selectedUrl || selectedOptions.background?.previewUrl,
			isUploading: imageUrl?.background?.isUploading,
		},
	];

	const optionButtons = modalType === 'product-staging' ? productStagingButtons : virtualTryonButtons;

	const visibleButtons = isLoadingOptions
		? []
		: optionButtons.filter((button) => {
				const optionData = options?.[button.panelId as keyof typeof options];
				if (button.panelId === 'background' && allowCustomBackground) return true;
				return button.required || (optionData && optionData.presets && optionData.presets.length > 0);
			});

	return (
		<ControlsContainer>
			<ControlsContent {...(isInsert ? { inert: 'inert' } : {})}>
				<ControlsInner>
					{inputImage && (
						<InputImagePreview>
							<InputImageContainer>
								<img src={inputImage} alt={T._('Input image for generation')} />
								<RemoveImageButton
									onClick={onClearInputImage}
									aria-label={T._('Remove input image')}
									title={T._('Remove input image')}
								>
									<CloseIcon aria-hidden='true' />
								</RemoveImageButton>
							</InputImageContainer>
							<InputImageLabel>{T._('Using this image as input')}</InputImageLabel>
						</InputImagePreview>
					)}

					<ControlsRow>
						{inputThumbnailUrl && (
							<InputThumbnail
								onClick={onExpandInputView}
								title={T._('View input image')}
								aria-label={T._('View input image')}
							>
								<img src={inputThumbnailUrl} alt={T._('Input product')} />
							</InputThumbnail>
						)}
						<ControlsColumn>
							<PromptWrapper>
								<PromptInput prompt={prompt} onPromptChange={onPromptChange} />
								<GenerateButton
									onClick={onGenerate}
									disabled={isLoadingGenerate}
									$isLoading={isLoadingGenerate}
									aria-label={isLoadingGenerate ? T._('Generating...') : T._('Generate AI image')}
								>
									{isLoadingGenerate ? (
										<StopIcon aria-hidden='true' />
									) : (
										<GenerateIcon aria-hidden='true' />
									)}
								</GenerateButton>
							</PromptWrapper>

							{visibleButtons.length > 0 && (
								<OptionsRow>
									<OptionsContainer role='group' aria-label={T._('AI image generation presets')}>
										{visibleButtons.map((option) => (
											<OptionButtonItem
												key={option.panelId}
												label={option.label}
												shortLabel={option.shortLabel}
												thumbnail={option.thumbnail}
												isActive={activePanel === option.panelId}
												isUploading={option.isUploading}
												menuId={innerMenuId}
												onClick={(e) => handleOptionClick(option.panelId, e)}
											/>
										))}
									</OptionsContainer>
								</OptionsRow>
							)}
						</ControlsColumn>
					</ControlsRow>
				</ControlsInner>
			</ControlsContent>
			{!isMobile && Array.from(dropdowns.values())}
			{mobileOptionPanelId && renderOptionPanel(mobileOptionPanelId)}
		</ControlsContainer>
	);
};

const ControlsContainer = styled.div`
	background-color: white;
	z-index: 1;
	flex-shrink: 0;
	padding: 20px;
	border-radius: 1.5rem;
	border-top: 1px solid rgb(219, 226, 230);

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		padding: 0;
		border-top: none;
	}
`;

const ControlsContent = styled.div``;

const ControlsInner = styled.div`
	max-width: 48rem;
	margin: 0 auto;
	border: 1px solid #e5e7eb;
	border-radius: 1.75rem;
	padding: 1rem;
`;

const ControlsRow = styled.div`
	display: flex;
	align-items: stretch;
	gap: 0.75rem;

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		flex-direction: column;
		align-items: center;
	}
`;

const ControlsColumn = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		width: 100%;
	}
`;

const InputThumbnail = styled.button`
	width: 60px;
	height: 60px;
	border-radius: 12px;
	overflow: hidden;
	border: 1px solid #e5e7eb;
	cursor: pointer;
	flex-shrink: 0;
	padding: 5px;
	background: #f9fafb;
	position: relative;
	transition: all 0.2s;

	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	&:hover {
		border-color: #c084fc;
		box-shadow: 0 0 0 2px rgba(192, 132, 252, 0.2);
	}

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		align-self: auto;
		border-radius: 8px;
	}
`;

const PromptWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	width: 100%;
	margin-bottom: 0.75rem;
`;

const OptionsRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	width: 100%;
	animation: optionsFadeIn 200ms ease-out 100ms backwards;

	@keyframes optionsFadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
`;

const OptionsContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	overflow-x: auto;
	flex: 1;
	-ms-overflow-style: none;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

const OptionButton = styled.button<{ active: boolean }>`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 8px;
	min-width: 100px;
	border-radius: 1.5rem;
	font-size: 13px;
	font-weight: 500;
	transition: all 0.2s;
	white-space: nowrap;
	flex-shrink: 0;
	border: 1px solid ${(props) => (props.active ? '#c084fc' : '#e5e7eb')};
	cursor: pointer;
	background-color: ${(props) => (props.active ? '#faf5ff' : 'white')};
	color: #374151;
	position: relative;

	&:hover:not(:disabled) {
		background-color: ${(props) => (props.active ? '#f3e8ff' : '#f9fafb')};
		border-color: ${(props) => (props.active ? '#a855f7' : '#d1d5db')};
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`;

const ThumbnailLoadingSpinner = styled.div`
	width: 1.25rem;
	height: 1.25rem;
	border: 2px solid #e5e7eb;
	border-top: 2px solid #a855f7;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	@media (min-width: 640px) {
		width: 1.5rem;
		height: 1.5rem;
	}
`;

const DesktopLabel = styled.span`
	display: none;

	@media (min-width: 640px) {
		display: inline;
	}
`;

const MobileLabel = styled.span`
	display: inline;

	@media (min-width: 640px) {
		display: none;
	}
`;

const Thumbnail = styled.div`
	width: 1.25rem;
	height: 1.25rem;
	border-radius: 9999px;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	@media (min-width: 640px) {
		width: 1.5rem;
		height: 1.5rem;
	}
`;

const EmptyThumbnail = styled.div`
	width: 1.25rem;
	height: 1.25rem;
	border-radius: 9999px;
	border: 1px dashed #9ca3af;
	display: flex;
	align-items: center;
	justify-content: center;
	color: rgb(156, 163, 175);
	position: relative;

	@media (min-width: 640px) {
		width: 1.5rem;
		height: 1.5rem;
	}
`;

const EmptyLine = styled.div`
	width: 60%;
	height: 1px;
	background-color: #9ca3af;
	transform: rotate(45deg);
	position: absolute;
`;

const IconThumbnail = styled.div`
	width: 1.25rem;
	height: 1.25rem;
	display: flex;
	align-items: center;
	justify-content: center;
	color: rgb(107, 114, 128);

	svg {
		width: 100%;
		height: 100%;
	}

	@media (min-width: 640px) {
		width: 1.5rem;
		height: 1.5rem;
	}
`;

const ChevronIcon = styled.div<{ active: boolean }>`
	width: 0.75rem;
	height: 0.75rem;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-left: 0.25rem;
	color: #9ca3af;
	transform: rotate(${(props) => (props.active ? '0deg' : '180deg')});
	transition: transform 200ms;

	svg {
		width: 100%;
		height: 100%;
	}
`;

const GenerateButton = styled.button<{ $isLoading?: boolean }>`
	width: 3rem;
	height: 3rem;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s;
	flex-shrink: 0;
	border: none;
	border-radius: 50%;
	background: ${(props) =>
		props.$isLoading ? '#ef4444' : 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'};
	cursor: pointer;

	svg {
		width: 1.5rem;
		height: 1.5rem;
		color: white;
		fill: white;
	}

	&:hover:not(:disabled) {
		background: ${(props) =>
			props.$isLoading ? '#dc2626' : 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)'};
		transform: scale(1.05);
	}

	&:disabled {
		opacity: ${(props) => (props.$isLoading ? '1' : '0.5')};
		cursor: ${(props) => (props.$isLoading ? 'default' : 'not-allowed')};
	}
`;

const StopIcon = styled.div`
	width: 16px;
	height: 16px;
	background-color: transparent;
	border: 2px solid white;
	border-radius: 3px;

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		width: 12px;
		height: 12px;
	}
`;

const InputImagePreview = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	margin-bottom: 0.75rem;
	padding: 0.75rem;
	background-color: #f9fafb;
	border-radius: 0.75rem;
	border: 1px dashed #d1d5db;
`;

const InputImageContainer = styled.div`
	position: relative;
	width: 120px;
	height: 120px;
	border-radius: 0.5rem;
	overflow: hidden;
	background-color: #f3f4f6;

	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		width: 100px;
		height: 100px;
	}
`;

const RemoveImageButton = styled.button`
	position: absolute;
	top: 0.25rem;
	right: 0.25rem;
	background: rgba(0, 0, 0, 0.7);
	border: none;
	border-radius: 50%;
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;
	z-index: 10;

	svg {
		width: 14px;
		height: 14px;
		fill: white;
	}

	&:hover {
		background: rgba(0, 0, 0, 0.9);
		transform: scale(1.1);
	}
`;

const InputImageLabel = styled.p`
	font-size: 0.75rem;
	color: #6b7280;
	margin: 0;
	text-align: center;
`;
